import React, { useEffect, useState } from 'react';
import { RefreshCw, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto, formatFecha } from '../utils/format';
import PendientesTasas from '../components/PendientesTasas';
import BotonAyuda from '../components/BotonAyuda';

const TIPOS_MARCA = {
  feriado_bancario: {
    label: 'Feriado / bancario',
    bg: 'bg-rose-50',
    bgHover: 'bg-rose-50 hover:bg-rose-100',
    border: 'border-rose-300',
    dot: 'bg-rose-500',
    text: 'text-rose-700'
  },
  evento: {
    label: 'Evento',
    bg: 'bg-amber-50',
    bgHover: 'bg-amber-50 hover:bg-amber-100',
    border: 'border-amber-300',
    dot: 'bg-amber-500',
    text: 'text-amber-700'
  }
};

const COLOR_BCV = '#0d9488';
const COLOR_USD = '#16a34a';
const DIAS_EVOLUCION = 30;

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];
const DIAS_SEMANA = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'];

function pad(n) {
  return String(n).padStart(2, '0');
}

function fechaHoy() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function diasEnMes(year, mes) {
  return new Date(year, mes + 1, 0).getDate();
}

function primerDiaSemana(year, mes) {
  return (new Date(year, mes, 1).getDay() + 6) % 7; // 0 = lunes
}

export default function TasasCambio() {
  const { empresaActiva } = useAuth();
  const [form, setForm] = useState({ fecha: fechaHoy(), tasa_bcv: '', tasa_usd_generica: '' });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const hoy = new Date();
  const [mesVista, setMesVista] = useState({ year: hoy.getFullYear(), mes: hoy.getMonth() });
  const [tasasMes, setTasasMes] = useState({});
  const [cargandoMes, setCargandoMes] = useState(true);
  const [evolucion, setEvolucion] = useState([]);
  const [diasMarcados, setDiasMarcados] = useState({});
  const [fechaMarcando, setFechaMarcando] = useState(null);

  async function cargarEvolucion() {
    const hasta = fechaHoy();
    const d = new Date();
    d.setDate(d.getDate() - (DIAS_EVOLUCION - 1));
    const desdeStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const { data } = await api.get('/tasas-cambio', { params: { desde: desdeStr, hasta } });
    setEvolucion(
      [...data]
        .sort((a, b) => (a.fecha < b.fecha ? -1 : 1))
        .map((t) => ({
          fecha: t.fecha,
          etiqueta: formatFecha(t.fecha).slice(0, 5),
          tasa_bcv: t.tasa_bcv != null ? Number(t.tasa_bcv) : null,
          tasa_usd_generica: t.tasa_usd_generica != null ? Number(t.tasa_usd_generica) : null
        }))
    );
  }

  async function cargarHoy() {
    const { data } = await api.get('/tasas-cambio/hoy');
    if (data) {
      setForm({ fecha: data.fecha, tasa_bcv: data.tasa_bcv, tasa_usd_generica: data.tasa_usd_generica ?? '' });
      return;
    }
    // Si hoy todavía no tiene tasa cargada, las tarjetas muestran el último día con datos.
    const { data: recientes } = await api.get('/tasas-cambio');
    const ultimo = recientes[0];
    if (ultimo) {
      setForm({ fecha: ultimo.fecha, tasa_bcv: ultimo.tasa_bcv, tasa_usd_generica: ultimo.tasa_usd_generica ?? '' });
      const [y, m] = ultimo.fecha.split('-').map(Number);
      setMesVista({ year: y, mes: m - 1 });
    }
  }

  async function cargarMes() {
    setCargandoMes(true);
    const desde = `${mesVista.year}-${pad(mesVista.mes + 1)}-01`;
    const hasta = `${mesVista.year}-${pad(mesVista.mes + 1)}-${pad(diasEnMes(mesVista.year, mesVista.mes))}`;
    const [{ data }, { data: marcados }] = await Promise.all([
      api.get('/tasas-cambio', { params: { desde, hasta } }),
      api.get('/dias-marcados', { params: { desde, hasta } })
    ]);
    setTasasMes(Object.fromEntries(data.map((t) => [t.fecha, t])));
    setDiasMarcados(Object.fromEntries(marcados.map((m) => [m.fecha, m])));
    setCargandoMes(false);
  }

  async function guardarMarca(fecha, tipo, nota) {
    const { data } = await api.post('/dias-marcados', { fecha, tipo, nota: nota || null });
    setDiasMarcados((prev) => ({ ...prev, [fecha]: data }));
    setFechaMarcando(null);
  }

  async function quitarMarca(fecha) {
    await api.delete(`/dias-marcados/${fecha}`);
    setDiasMarcados((prev) => {
      const copia = { ...prev };
      delete copia[fecha];
      return copia;
    });
    setFechaMarcando(null);
  }

  useEffect(() => {
    if (!empresaActiva) return;
    cargarHoy();
    cargarEvolucion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva]);

  useEffect(() => {
    if (!empresaActiva) return;
    cargarMes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva, mesVista]);

  async function guardar(e) {
    e.preventDefault();
    setError('');
    setGuardando(true);
    try {
      await api.post('/tasas-cambio', form);
      await Promise.all([cargarHoy(), cargarMes(), cargarEvolucion()]);
      window.dispatchEvent(new Event('tasas-cambio:actualizado'));
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar la tasa de cambio');
    } finally {
      setGuardando(false);
    }
  }

  function cambiarMes(delta) {
    setMesVista(({ year, mes }) => {
      const total = year * 12 + mes + delta;
      return { year: Math.floor(total / 12), mes: ((total % 12) + 12) % 12 };
    });
  }

  function seleccionarDia(fecha) {
    const dato = tasasMes[fecha];
    setForm({
      fecha,
      tasa_bcv: dato ? dato.tasa_bcv : '',
      tasa_usd_generica: dato && dato.tasa_usd_generica != null ? dato.tasa_usd_generica : ''
    });
  }

  function irAFecha(p) {
    const [y, m] = p.fecha.split('-').map(Number);
    setMesVista({ year: y, mes: m - 1 });
    // Un día con la BCV ya cargada (p. ej. automática) conserva ese valor.
    setForm({ fecha: p.fecha, tasa_bcv: p.tasa_bcv ?? '', tasa_usd_generica: p.tasa_usd_generica ?? '' });
  }

  const datoSeleccionado = tasasMes[form.fecha];
  const bcvAutomatica = Boolean(datoSeleccionado && datoSeleccionado.capturado_por == null);

  const celdas = [];
  const offset = primerDiaSemana(mesVista.year, mesVista.mes);
  for (let i = 0; i < offset; i++) celdas.push(null);
  for (let dia = 1; dia <= diasEnMes(mesVista.year, mesVista.mes); dia++) celdas.push(dia);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-800">Tasas de Cambio<BotonAyuda /></h1>

      <PendientesTasas onSeleccionar={irAFecha} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-xl shadow-sm p-4">
            <p className="text-xs text-slate-500 mb-1">
              Tasa BCV · {form.fecha === fechaHoy() ? 'hoy' : formatFecha(form.fecha)}
            </p>
            <p className="text-xl font-bold text-slate-800 tabular-nums">
              {form.tasa_bcv !== '' ? formatMonto(form.tasa_bcv, 2) : '—'}
            </p>
            <p className="text-[11px] text-slate-400 mt-1">
              Contabilidad y precios{bcvAutomatica && ' · automática de bcv.org.ve'}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <p className="text-xs text-slate-500 mb-1">
              Tasa USD · {form.fecha === fechaHoy() ? 'hoy' : formatFecha(form.fecha)}
            </p>
            <p className="text-xl font-bold text-slate-800 tabular-nums">
              {form.tasa_usd_generica !== '' ? formatMonto(form.tasa_usd_generica, 2) : '—'}
            </p>
            <p className="text-[11px] text-slate-400 mt-1">Dólar; puede sobreescribirse por movimiento</p>
          </div>
        </div>
        <GraficoEvolucionTasas datos={evolucion} />
      </div>

      <form onSubmit={guardar} className="bg-white rounded-xl shadow-sm p-5 grid grid-cols-1 sm:grid-cols-4 gap-4">
        <p className="sm:col-span-4 font-medium text-slate-600 flex items-center gap-2">
          <RefreshCw size={16} /> Actualizar tasa de cambio
        </p>

        <input
          required
          type="date"
          value={form.fecha}
          onChange={(e) => setForm({ ...form, fecha: e.target.value })}
          className="border rounded-md px-3 py-2"
        />

        <input
          required
          type="number"
          step="0.0001"
          placeholder="Tasa BCV"
          value={form.tasa_bcv}
          onChange={(e) => setForm({ ...form, tasa_bcv: e.target.value })}
          className="border rounded-md px-3 py-2"
        />

        <input
          required
          type="number"
          step="0.0001"
          placeholder="Tasa USD"
          value={form.tasa_usd_generica}
          onChange={(e) => setForm({ ...form, tasa_usd_generica: e.target.value })}
          className="border rounded-md px-3 py-2"
        />

        <button
          type="submit"
          disabled={guardando}
          className="bg-primary-600 text-white rounded-md py-2 hover:bg-primary-700 disabled:opacity-50"
        >
          {guardando ? 'Guardando...' : 'Guardar'}
        </button>

        {error && <p className="text-red-600 text-sm sm:col-span-4">{error}</p>}
      </form>

      <div className="bg-white rounded-xl shadow-sm p-5 lg:w-[70%] mx-auto">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => cambiarMes(-1)}
            className="p-2 rounded-md text-slate-500 hover:bg-slate-100 transition-colors"
            title="Mes anterior"
          >
            <ChevronLeft size={18} />
          </button>
          <p className="font-medium text-slate-700">{MESES[mesVista.mes]} {mesVista.year}</p>
          <button
            onClick={() => cambiarMes(1)}
            className="p-2 rounded-md text-slate-500 hover:bg-slate-100 transition-colors"
            title="Mes siguiente"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-2 mb-2">
          {DIAS_SEMANA.map((d) => (
            <div key={d} className="text-center text-sm font-semibold text-slate-400 py-1">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-2">
          {celdas.map((dia, idx) => {
            if (dia === null) return <div key={`vacio-${idx}`} />;
            const fecha = `${mesVista.year}-${pad(mesVista.mes + 1)}-${pad(dia)}`;
            const dato = tasasMes[fecha];
            const marca = diasMarcados[fecha];
            const tipoMarca = marca ? TIPOS_MARCA[marca.tipo] : null;
            const columna = idx % 7;
            const esFinDeSemana = columna === 5 || columna === 6;
            const esHoy = fecha === fechaHoy();
            const seleccionado = fecha === form.fecha;
            const completo = Boolean(dato && dato.tasa_bcv != null && dato.tasa_usd_generica != null);
            const mostrarEstado = !cargandoMes && fecha <= fechaHoy();

            let claseFondo = dato ? 'bg-white hover:bg-slate-50' : 'bg-slate-50/50 hover:bg-slate-50';
            if (esFinDeSemana) claseFondo = 'bg-green-50/70 hover:bg-green-50';
            if (esHoy) claseFondo = 'bg-primary-50';
            if (tipoMarca) claseFondo = tipoMarca.bgHover;

            return (
              <button
                key={fecha}
                type="button"
                onClick={() => seleccionarDia(fecha)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setFechaMarcando(fecha);
                }}
                title={tipoMarca ? `${tipoMarca.label}${marca.nota ? ': ' + marca.nota : ''}` : 'Clic derecho para marcar el día'}
                className={`flex flex-col rounded-lg border p-2 min-h-[92px] text-left transition-colors
                  ${seleccionado ? 'border-primary-500 ring-1 ring-primary-500' : tipoMarca ? tipoMarca.border : 'border-slate-100'}
                  ${claseFondo}`}
              >
                <span className="flex items-center gap-1.5 mb-1">
                  <span className={`text-base font-semibold ${esHoy ? 'text-primary-700' : 'text-slate-600'}`}>{dia}</span>
                  {mostrarEstado && (
                    <span
                      title={completo ? 'Tasas completas' : 'Falta alguna tasa'}
                      className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${completo ? 'bg-green-500' : 'bg-red-500'}`}
                    />
                  )}
                  {tipoMarca && (
                    <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ml-auto ${tipoMarca.dot}`} />
                  )}
                </span>
                {cargandoMes ? null : dato ? (
                  <div className="mt-auto w-full space-y-0.5 text-slate-700">
                    <div className="flex items-baseline justify-between gap-1 leading-tight">
                      <span className="text-[10px] font-semibold text-slate-400">BCV</span>
                      <span className="text-[13px] font-medium tabular-nums">{formatMonto(dato.tasa_bcv, 2)}</span>
                    </div>
                    <div className="flex items-baseline justify-between gap-1 leading-tight">
                      <span className="text-[10px] font-semibold text-slate-400">USD</span>
                      <span className="text-[13px] font-medium tabular-nums">
                        {dato.tasa_usd_generica != null ? formatMonto(dato.tasa_usd_generica, 2) : '—'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <span className="text-xs text-slate-300 mt-auto">Sin datos</span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-4 mt-4 pt-3 border-t text-[11px] text-slate-500">
          <LeyendaMarca className="bg-green-50/70 border border-green-200" label="Fin de semana" />
          <LeyendaMarca className={TIPOS_MARCA.feriado_bancario.dot} label="Feriado / bancario" />
          <LeyendaMarca className={TIPOS_MARCA.evento.dot} label="Evento" />
          <span className="ml-auto text-slate-400">Clic derecho en un día para marcarlo</span>
        </div>
      </div>

      {fechaMarcando && (
        <PopoverMarcarDia
          fecha={fechaMarcando}
          marca={diasMarcados[fechaMarcando]}
          onGuardar={guardarMarca}
          onQuitar={quitarMarca}
          onClose={() => setFechaMarcando(null)}
        />
      )}
    </div>
  );
}

function LeyendaMarca({ className, label }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={`h-2.5 w-2.5 rounded-full inline-block ${className}`} />
      {label}
    </span>
  );
}

function PopoverMarcarDia({ fecha, marca, onGuardar, onQuitar, onClose }) {
  const [tipo, setTipo] = useState(marca?.tipo || 'feriado_bancario');
  const [nota, setNota] = useState(marca?.nota || '');
  const [guardando, setGuardando] = useState(false);

  async function handleGuardar() {
    setGuardando(true);
    try {
      await onGuardar(fecha, tipo, nota.trim());
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-lg w-full max-w-xs p-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-800 text-sm">Marcar {formatFecha(fecha)}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={16} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-3">
          {Object.entries(TIPOS_MARCA).map(([clave, t]) => (
            <button
              key={clave}
              type="button"
              onClick={() => setTipo(clave)}
              className={`rounded-md border px-2 py-2 text-xs font-medium transition-colors ${
                tipo === clave ? `${t.border} ${t.bg} ${t.text}` : 'border-slate-200 text-slate-500 hover:bg-slate-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <input
          type="text"
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          placeholder="Nota (opcional)"
          maxLength={255}
          className="border rounded-md px-3 py-2 w-full text-sm mb-3"
        />

        <div className="flex items-center gap-2">
          {marca && (
            <button
              type="button"
              onClick={() => onQuitar(fecha)}
              className="text-red-600 hover:text-red-700 text-xs font-medium mr-auto"
            >
              Quitar marca
            </button>
          )}
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700">
            Cancelar
          </button>
          <button
            type="button"
            disabled={guardando}
            onClick={handleGuardar}
            className="bg-primary-600 text-white rounded-md px-3 py-1.5 text-xs font-medium hover:bg-primary-700 disabled:opacity-50"
          >
            {guardando ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Evolución de BCV/USD de los últimos 30 días y brecha cambiaria (% que el
// USD genérico se separa de la oficial) — da el pulso inflacionario/
// diferencial sin salir de la pantalla de carga de tasas.
function GraficoEvolucionTasas({ datos }) {
  const conDatos = datos.filter((d) => d.tasa_bcv != null || d.tasa_usd_generica != null);
  const ultimaBrecha = [...datos].reverse().find((d) => d.tasa_bcv > 0 && d.tasa_usd_generica > 0);
  const brecha = ultimaBrecha ? ((ultimaBrecha.tasa_usd_generica - ultimaBrecha.tasa_bcv) / ultimaBrecha.tasa_bcv) * 100 : null;

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 flex flex-col">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-slate-700 leading-tight">Evolución BCV / USD</h2>
          <p className="text-[11px] text-slate-400 mt-0.5">Últimos {DIAS_EVOLUCION} días</p>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-slate-500 shrink-0">
          <LeyendaEvolucion color={COLOR_BCV} label="BCV" />
          <LeyendaEvolucion color={COLOR_USD} label="USD" />
        </div>
      </div>

      {brecha != null && (
        <span
          title="Brecha cambiaria: cuánto se separa el USD genérico de la tasa BCV oficial"
          className={`self-start mt-2 text-[11px] font-semibold rounded-full px-2 py-0.5 ${
            brecha >= 0 ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'
          }`}
        >
          Brecha {brecha >= 0 ? '+' : ''}{formatMonto(brecha, 1)}%
        </span>
      )}

      {conDatos.length < 2 ? (
        <p className="text-slate-400 text-sm py-8 text-center flex-1">Sin suficientes tasas cargadas todavía</p>
      ) : (
        <div className="mt-2 flex-1">
          <ResponsiveContainer width="100%" height={110}>
            <LineChart data={datos} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
              <XAxis dataKey="etiqueta" hide />
              <YAxis hide domain={['auto', 'auto']} />
              <Tooltip content={<TooltipEvolucion />} />
              <Line type="monotone" dataKey="tasa_bcv" name="BCV" stroke={COLOR_BCV} strokeWidth={1.6} dot={false} connectNulls activeDot={{ r: 3 }} />
              <Line type="monotone" dataKey="tasa_usd_generica" name="USD" stroke={COLOR_USD} strokeWidth={1.6} dot={false} connectNulls activeDot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function LeyendaEvolucion({ color, label }) {
  return (
    <span className="flex items-center gap-1">
      <span className="w-2 h-2 rounded-full inline-block" style={{ background: color }} />
      {label}
    </span>
  );
}

function TooltipEvolucion({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div
      className="text-xs shadow-lg"
      style={{
        background: '#131d26',
        border: '1px solid rgba(255,255,255,0.14)',
        borderRadius: 8,
        padding: '8px 10px',
        minWidth: 110,
        color: '#fff'
      }}
    >
      <p className="text-[11px] text-slate-300 mb-1">{label}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-3 leading-5">
          <span className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: p.stroke }} />
            {p.name}
          </span>
          <span className="font-medium">{p.value != null ? formatMonto(p.value, 2) : '—'}</span>
        </div>
      ))}
    </div>
  );
}
